import React, { useMemo } from 'react';

import i18n from '@nextroll/ar-i18n';
import { Alerts, InlineAlert } from '@nextroll/ar-style-base';

import { CrmOptionType } from 'types/rofl';


export const AccountActionCrmMappingSection = ({ advertisableEid, crm: rawCrm, value, isError, isWarning }: {
    advertisableEid?: string;
    crm?: CrmOptionType;
    value?: string;
    isError?: boolean;
    isWarning?: boolean;
}) => {
    const crm = useMemo(() => (rawCrm === 'salesforce' ? 'Salesforce' : 'HubSpot'), [rawCrm]);

    const errorMsg = useMemo(() => {
        if (value === 'Contact') {
            return i18n.gettext('Unable to create {{ crm }} contacts', { crm });
        }
        if (value === 'Lead') {
            return i18n.gettext('Unable to create {{ crm }} leads', { crm });
        }
        if (value === 'Contact And Account' && rawCrm === 'salesforce') {
            return i18n.gettext('Unable to create Salesforce contacts and accounts');
        }
        if (value === 'Contact And Account') { // HubSpot
            return i18n.gettext('Unable to create HubSpot contacts and companies');
        }
        return i18n.gettext('Unable to create {{ crm }} contacts and leads', { crm });
    }, [rawCrm, crm, value]);

    const mapping = useMemo(() => {
        if (value === 'Contact' && rawCrm === 'salesforce') {
            return i18n.gettext('Contact records will be created in Salesforce using field mapping defined in Settings.');
        }
        if (value === 'Contact') { // HubSpot
            return i18n.gettext('Contact records will be created in HubSpot using field mapping defined in Settings.');
        }
        if (value === 'Lead') {
            return i18n.gettext('Lead records will be created in {{ crm }} using field mapping defined in Settings.', { crm });
        }
        if (value === 'Contact And Account' && rawCrm === 'salesforce') {
            return i18n.gettext('New Contact and Account records will be created in Salesforce using field mapping defined in Settings.');
        }
        if (value === 'Contact And Account') { // HubSpot
            return i18n.gettext('New Contact and Company records will be created in HubSpot using field mapping defined in Settings.');
        }
        return i18n.gettext('New Lead and Account records will be created in {{ crm }} using field mapping defined in Settings.', { crm });
    }, [rawCrm, crm, value]);

    const link = useMemo(() => (
        <a href={`/settings/${rawCrm}?advertisable=${advertisableEid}#fieldmapping`} rel='noopener noreferrer' target='_blank'>
            {i18n.gettext('Edit Mapping')}
            &nbsp;
            <i className='ar-icon ar-icon-fw fa-external-link-alt' />
        </a>
    ), [advertisableEid, rawCrm]);

    if (!value || !advertisableEid || !rawCrm) {
        return null;
    }

    return (
        <>
            {isError && (
                <InlineAlert
                    type={Alerts.ALERT_TYPE_ERROR}
                    title={errorMsg}
                    message={i18n.gettext('Required field(s) are missing mappings. Please map the appropriate fields before proceeding.  <link />', { link })}
                    showIcon
                />
            )}
            {isWarning && (
                <InlineAlert
                    type={Alerts.ALERT_TYPE_WARNING}
                    title={i18n.gettext('Unable to write data to one or more {{ crm }} Fields', { crm })}
                    message={i18n.gettext('If you proceed, Adroll ABM will skip the missing {{ crm }} Field(s) and push data only to available fields.  <link />', { crm, link })}
                    showIcon
                />
            )}
            {(isError || isWarning) && <br />}

            <p><strong>{i18n.gettext('Mapping')}</strong></p>
            <p>{mapping}</p>
            {link}
        </>
    );
};
